import { useEffect, useMemo, useState } from "react";
import Profile from "./profile/profile";
import ApiSubjectsManagement from "../../../api/subjects-manager";
import { useLocation, useParams } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import Surveys from "./surveys/surveys";
import SubjectTasks from "./tasks/tasks";
import { MainLayoutV2 } from "../../../components/main-layout-v2/main-layout-v2";
import { Sidebar } from "../../../components/sidebar/sidebar";
import { MainNavigation } from "../../../components/main-navigation/main-navigation";
import HealthData from "./health/health";
import Plan from "./plan/plan";
import Uploads from "./uploads/uploads";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectUser, selectCurrentProject } from "../../../store/slices/user";
import UnifiedAiButton from "../../../components/UnifiedAiButton/UnifiedAiButton";
import PageContentLoader from "../../../components/loader/page-content-loader";
import useAdminEvents from "../../../hooks/useAdminEvents";

const SubjectLayout = () => {
  const defaultStartDate = moment().subtract(30, "days").utc().toISOString();
  const defaultEndDate = moment().utc().toISOString();
  const { pathname, hash } = useLocation();
  const { id } = useParams();

  const currentUser = useSelector(selectUser);
  const currentProject = useSelector(selectCurrentProject);

  const [subject, setSubject] = useState();
  const [subjectLoading, setSubjectLoading] = useState(true);
  const [survey, setSurvey] = useState();
  const [tasks, setTasks] = useState();
  const [analytics, setAnalytics] = useState();
  const [titles, setTitles] = useState();
  const [filters, setFilters] = useState({
    period: [{ start: defaultStartDate, end: defaultEndDate }],
  });

  const navigate = useProjectNavigate();

  const getSubject = () => {
    setSubjectLoading(true);

    ApiSubjectsManagement.getSubject(id)
      .then((r) => {
        setSubject(r.data.subject);
      })
      .finally(() => {
        setSubjectLoading(false);
      });
  };

  useAdminEvents({
    onSubjectChanged: (payload) => {
      const changedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      if (!changedIds.includes(id)) return;

      if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
        navigate("/subject-management");
        return;
      }

      if (payload?.action === "updated" && payload?.subject) {
        setSubject((prev) =>
          prev?.id === payload.subject.id
            ? { ...prev, ...payload.subject }
            : prev
        );
        return;
      }

      getSubject();
    },
  });

  const getSurvey = () => {
    ApiSubjectsManagement.getSubjectSurveys(subject?.id || id).then((r) => {
      setSurvey(r.data);
    });
  };

  const getSubjectTasks = () => {
    const params = {
      ...filters,
      startDueDate: filters.period?.[filters.period.length - 1]?.start,
      endDueDate: filters.period?.[filters.period.length - 1]?.end,
    };

    ApiSubjectsManagement.getSubjectTasks(subject?.id || id, params).then((r) => {
      setTasks(r.data.subjectTasks);
      setAnalytics(r.data.analytics);
      setTitles(r.data.titles);
    });
  };

  useEffect(() => {
    getSubject();
  }, [id]);

  useEffect(() => {
    if (subject?.id) {
      getSurvey();
    }
  }, [subject?.id]);

  useEffect(() => {
    if (!hash) {
      navigate(`${pathname}#profile`, { replace: true });
    }
  }, [hash, pathname, navigate]);

  useEffect(() => {
    if (subject?.id) {
      getSubjectTasks();
    }
  }, [subject?.id, filters]);

  const updateFilterValue = (filter) => {
    setFilters(filter);
  };

  const leftSideNavItems = [
    {
      key: `${pathname}#profile`,
      label: "Profile data",
      default: true,
    },
    {
      key: `${pathname}#tasks`,
      label: "Tasks",
    },
    {
      key: `${pathname}#plan`,
      label: "Plan",
    },
    {
      key: `${pathname}#surveys`,
      label: "Onboarding",
    },
    {
      key: `${pathname}#health`,
      label: "Health",
    },
    {
      key: `${pathname}#uploads`,
      label: "Uploads",
    },
  ];

  const content = useMemo(() => {
    if (subjectLoading) {
      return (
        <div style={{ position: "relative", width: "100%" }}>
          <PageContentLoader />
        </div>
      );
    }

    if (!subject) return null;

    switch (hash) {
      default:
      case "#profile":
        return <Profile subject={subject} fetchSubject={getSubject} />;
      case "#tasks":
        return (
          <SubjectTasks
            tasks={tasks}
            analytics={analytics}
            updateFilterValue={updateFilterValue}
            titles={titles}
            planWeek={subject.weekIndex + 1}
            currentWeek={subject.currentWeek}
            filters={filters}
          />
        );

      case "#plan":
        return (
          <Plan
            subjectId={subject.id}
            startedAt={subject.startedAt}
            timezone={subject.timeZone}
          />
        );

      case "#surveys":
        return <Surveys survey={survey} />;

      case "#health":
        return <HealthData />;

      case "#uploads":
        return <Uploads />;
    }
  }, [hash, subject, survey, tasks, analytics, titles, filters, subjectLoading]);

  const firstName =
    subject?.enrollmentData.find((f) => f.label === "First name")?.value || "";
  const lastName =
    subject?.enrollmentData.find((f) => f.label === "Last name")?.value || "";

  const subjectContext = useMemo(() => {
    if (!subject) return null;

    const currentTab = hash?.replace("#", "") || "tasks";

    return {
      currentRoute: pathname,
      projectId: currentProject?.id,
      activeFilters: filters,
      visibleDataSummary: {
        subjectId: subject.id,
        displayId: subject.displayId,
        email: subject.email,
        firstName,
        lastName,
        status: subject.status,
        cohort: subject.enrollmentData.find((f) => f.label === "Cohort")?.value,
        weekInProgram: subject.weekIndex + 1,
        lastActivity: subject.lastActivity,
        timeZone: subject.timeZone,
        startedAt: subject.startedAt,
        currentTab,

        enrollmentData: subject.enrollmentData || [],

        tasksLoaded: !!tasks,
        tasksCount: tasks?.length || 0,
        tasksSummary: tasks
          ? {
              total: tasks.length,
              completed: tasks.filter((t) => t.status === "COMPLETED").length,
              pending: tasks.filter(
                (t) => t.status === "PENDING" || t.status === "SCHEDULED"
              ).length,
              dismissed: tasks.filter((t) => t.status === "DISMISSED").length,
              incomplete: tasks.filter((t) => t.status === "INCOMPLETED").length,
              missed: tasks.filter((t) => t.status === "MISSED").length,
            }
          : null,

        recentTasks: tasks?.slice(0, 10).map((t) => ({
          title: t.title,
          status: t.status,
          completedAt: t.completedAt,
          dueDate: t.dueDate,
        })),

        analyticsLoaded: !!analytics,
        analytics: analytics
          ? {
              completionRate: analytics.completionRate,
              adherenceRate: analytics.adherenceRate,
              averageResponseTime: analytics.averageResponseTime,
            }
          : null,

        surveyLoaded: !!survey,
        onboardingData: survey
          ? {
              total: survey.total || 0,
              completed: survey.completed || 0,
              completionRate: survey.rate || 0,
              surveys:
                survey.subjectSurveys?.map((s) => ({
                  status: s.status,
                  startedAt: s.startedAt,
                  completedAt: s.completedAt,
                  surveyTitle: s.OnboardingSurvey?.title,
                  responsesCount: s.response?.length || 0,
                })) || [],
            }
          : null,

        dateRangeFilter: filters.period?.[filters.period.length - 1],
      },
    };
  }, [
    subject,
    pathname,
    currentProject,
    hash,
    firstName,
    lastName,
    tasks,
    analytics,
    survey,
    filters,
  ]);

  return (
    <MainLayoutV2>
      <MainNavigation />

      <Sidebar
        items={leftSideNavItems}
        onBack={() => {
          navigate("/subject-management");
        }}
        title={subjectLoading ? "" : `${subject?.displayId || ""}`}
        titleTestID="subject-title"
        subjectInfo={
          !subjectLoading && subject
            ? {
                firstName,
                lastName,
                lastActivity: subject.lastActivity,
                status: subject.status,
                personStatus: subject.personStatus,
                personId: subject.personId,
                subjectId: subject.id,
                onUnblocked: getSubject,
              }
            : null
        }
      />

      {content}

      {subject && currentUser && subjectContext && (
        <UnifiedAiButton
          userId={currentUser.id}
          currentRoute={pathname}
          projectId={currentProject && currentProject.id}
          contextData={subjectContext}
        />
      )}
    </MainLayoutV2>
  );
};

export default SubjectLayout;
